import React, { useEffect, useRef } from 'react';

const steps = [
  {
    num: '01',
    title: 'Discover',
    desc: 'We start with a real conversation. Not a sales pitch. We dig into how your business actually runs, where the friction is, and what a good outcome looks like. No assumptions, no templates.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
    ),
  },
  {
    num: '02',
    title: 'Architect',
    desc: 'Once we understand the problem, we design the solution: tech stack, system boundaries, integrations. You review the full plan before a single line of code gets written.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 17.5h7M17.5 14v7"/>
      </svg>
    ),
  },
  {
    num: '03',
    title: 'Build',
    desc: 'We build in tight, focused sprints with short feedback loops, clear milestones, and a working product you can see throughout. No mystery deliveries at the end of a long silence.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        <line x1="12" y1="2" x2="12" y2="22"/>
      </svg>
    ),
  },
  {
    num: '04',
    title: 'Launch & Scale',
    desc: 'We handle deployment, monitoring, and handoff. Then we stick around. As your business grows, we make sure the systems we built grow with it without you having to start over.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
      </svg>
    ),
  },
];

const Process: React.FC = () => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 110);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="process" id="process" ref={ref}>
      <div className="container">
        <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 64px' }}>
          <span className="section-label" style={{ justifyContent: 'center' }}>How We Work</span>
          <h2>A process built for<br /><span className="gt">real projects</span></h2>
          <p style={{ margin: '0 auto', textAlign: 'center' }}>Clear steps, no surprises. Every engagement runs the same way.</p>
        </div>

        <div className="process-grid">
          {steps.map((step, i) => (
            <div className={`process-card reveal d${i + 1}`} key={step.num}>
              <div className="process-num-bg">{step.num}</div>
              <div className="process-top">
                <span className="process-badge">{step.num}</span>
                <div className="process-icon">{step.icon}</div>
              </div>
              <h3 className="process-title">{step.title}</h3>
              <p className="process-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
